import classnames from "classnames"
import PropTypes from "prop-types"
import React from "react"
import Select, * as SelectGoodies from "react-select"
import FieldWrapper from "../form-utils/FieldWrapper"

export const components = SelectGoodies.components

const mergeStyles = (baseStyles, stylesProp) => {
  if (!stylesProp) return baseStyles

  const merged = { ...baseStyles }
  for (const key of Object.keys(stylesProp)) {
    const consumerStyle = stylesProp[key]
    const baseStyle = baseStyles[key]
    merged[key] = (provided, state) => {
      const fromBase = baseStyle ? baseStyle(provided, state) : provided
      return typeof consumerStyle === "function"
        ? consumerStyle(fromBase, state)
        : { ...fromBase, ...consumerStyle }
    }
  }
  return merged
}

export const getReactSelectInputStyles = ({
  minimal = false,
  inline = false,
  // Reimplements v1 autosize: size the control to the selected value.
  autosize = false,
  position = "bottom",
  styles: stylesProp
} = {}) => {
  // v5 absolutely positions singleValue, so it no longer contributes to width
  // unless we force static layout (legacy inline/minimal/autosize behavior).
  const fitContent = Boolean(minimal || inline || autosize)

  const baseStyles = {
    container: (provided) => ({
      ...provided,
      ...(fitContent
        ? {
            display: "inline-block",
            width: "auto",
            verticalAlign: "middle"
          }
        : {})
    }),
    control: (provided, state) => ({
      ...provided,
      boxShadow: "none",
      backgroundColor: "transparent",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      ...(fitContent
        ? {
            display: "inline-flex",
            width: "auto",
            minWidth: "min-content",
            flexWrap: "nowrap"
          }
        : {}),
      ...(minimal
        ? {
            minHeight: 25,
            height: 25,
            padding: 0,
            borderRadius: 0,
            borderWidth: 0,
            borderStyle: "solid",
            borderColor: "transparent",
            // Keep a stable 1px bottom border always so opening/closing the
            // menu does not flash a border in/out.
            borderBottomWidth: 1,
            borderBottomStyle: state.isFocused ? "solid" : "dotted",
            borderBottomColor: state.isFocused ? "#6a9fcb" : "#888"
          }
        : {
            borderRadius: 4,
            // Keep all borders present while the menu is open to avoid a
            // bottom-border flash when the menu closes/blurs.
            border: `1px solid ${
              state.isFocused || state.menuIsOpen ? "#6a9fcb" : "#ddd"
            }`,
            borderBottomRightRadius: state.menuIsOpen ? 0 : 4,
            borderBottomLeftRadius: state.menuIsOpen ? 0 : 4,
            ...(state.isDisabled ? { borderColor: "transparent" } : {})
          })
    }),
    valueContainer: (provided) => ({
      ...provided,
      ...(minimal ? { padding: 0 } : {}),
      ...(fitContent
        ? {
            display: "flex",
            flexWrap: "nowrap",
            overflow: "visible"
          }
        : {})
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      ...(fitContent
        ? {
            // Keep the dummy/search input from forcing extra width.
            position: "absolute",
            top: 0,
            left: 0
          }
        : {})
    }),
    placeholder: (provided) => ({
      ...provided,
      ...(minimal ? { margin: 0, padding: 0 } : {}),
      ...(fitContent
        ? {
            position: "static",
            transform: "none",
            maxWidth: "none",
            whiteSpace: "nowrap"
          }
        : {})
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? "#888" : "#222",
      ...(minimal ? { margin: 0, padding: 0 } : {}),
      ...(fitContent
        ? {
            position: "static",
            transform: "none",
            maxWidth: "none",
            whiteSpace: "nowrap"
          }
        : {})
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "rgba(0, 0, 0, 0.1)",
      borderRadius: 50
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#222"
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#222",
      borderRadius: "0 50px 50px 0",
      ":hover": {
        backgroundColor: "rgba(0, 0, 0, 0.1)",
        color: "#222"
      }
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? provided.color : "#222",
      backgroundColor:
        state.isFocused && !state.isSelected
          ? "rgba(0, 0, 0, 0.05)"
          : provided.backgroundColor,
      ":active": {
        ...provided[":active"],
        backgroundColor: state.isSelected
          ? provided.backgroundColor
          : "rgba(0, 0, 0, 0.1)"
      }
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999999990,
      borderRadius: position === "top" ? "4px 4px 0 0" : "0 0 4px 4px",
      border: "1px solid #6a9fcb",
      boxShadow: "none",
      // Overlap the control's border so the menu still reads as connected
      // without toggling the control's bottom border on open/close.
      ...(position === "top"
        ? { marginBottom: -1, borderBottom: "none" }
        : { marginTop: -1, borderTop: "none" })
    }),
    indicatorSeparator: () => ({
      display: "none"
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      ...(minimal ? { padding: "0 0 0 4px" } : {}),
      ...(fitContent ? { flexShrink: 0 } : {})
    })
  }

  return mergeStyles(baseStyles, stylesProp)
}

const SelectInput = function (props) {
  const {
    label,
    value,
    className,
    readOnly,
    disabled,
    onChange,
    onInputChange,
    searchable = false,
    clearable = false,
    multi = false,
    isSearchable,
    isClearable,
    isMulti,
    isDisabled,
    position = "bottom",
    styles: stylesProp,
    minimal,
    inline,
    autosize = false,
    children,
    openOnFocus,
    openMenuOnFocus,
    // FieldWrapper / layout props — do not forward to Select
    labelWidth,
    topLabel,
    hideStatus,
    hideLabel,
    hideCaution,
    collapse,
    layoutAsColumns,
    helperText,
    tooltip,
    instructions,
    errors,
    error,
    required,
    validating,
    custom,
    stretch,
    inverted,
    style,
    alignCenter,
    alignRight,
    // Removed v1 react-select props — strip so they are not forwarded
    menuStyle,
    wrapperStyle,
    menuContainerStyle,
    styleType,
    simpleValue,
    labelKey,
    valueKey,
    valueRenderer,
    optionRenderer,
    noResultsText,
    ...selectProps
  } = props

  const selectStyles = getReactSelectInputStyles({
    minimal,
    inline,
    autosize,
    position,
    styles: stylesProp
  })

  return (
    <FieldWrapper {...props} className={classnames("SelectInput", className)}>
      {({ placeholder, onFocus, onBlur }) => {
        return (
          <React.Fragment>
            <Select
              {...selectProps}
              classNamePrefix="Select"
              menuPlacement={position === "top" ? "top" : "bottom"}
              name={typeof label === "string" ? label : selectProps.name}
              value={value ?? null}
              isSearchable={isSearchable ?? searchable}
              isClearable={isClearable ?? clearable}
              isMulti={isMulti ?? multi}
              isDisabled={Boolean(isDisabled || disabled || readOnly)}
              placeholder={placeholder}
              onChange={onChange}
              onInputChange={onInputChange}
              onFocus={onFocus}
              onBlur={onBlur}
              openMenuOnFocus={openMenuOnFocus ?? openOnFocus}
              styles={selectStyles}
            />
            {children}
          </React.Fragment>
        )
      }}
    </FieldWrapper>
  )
}

SelectInput.displayName = "SelectInput"

SelectInput.propTypes = {
  position: PropTypes.oneOf(["top", "bottom"]) // position of menu
}

export default SelectInput
